import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useCart } from '@/context/CartContext';
import { formatRSD } from '@/lib/currency';
import { createOrderFromCheckout } from '@/lib/orders';
import { sendOrderConfirmationEmail } from '@/lib/email';

export default function CartPage() {
  const { items, removeItem, updateQuantity, totalPrice, clearCart } = useCart();
  const [showCheckout, setShowCheckout] = useState(false);
  const [form, setForm] = useState({ fullName: '', email: '', address: '', city: '', phone: '' });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [orderSuccess, setOrderSuccess] = useState<number | null>(null);

  const setField = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((p) => ({ ...p, [key]: e.target.value }));
    setFormErrors((p) => ({ ...p, [key]: '' }));
  };

  const shippingCost = totalPrice >= 5000 ? 0 : 300;

  const handleSubmitOrder = async () => {
    const errors: Record<string, string> = {};
    if (!form.fullName.trim()) errors.fullName = 'Obavezno polje';
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) errors.email = 'Unesite ispravan email';
    if (!form.address.trim()) errors.address = 'Obavezno polje';
    if (!form.city.trim()) errors.city = 'Obavezno polje';
    if (!form.phone.trim()) errors.phone = 'Obavezno polje';
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    const result = await createOrderFromCheckout(
      form,
      items.map((it) => ({ id: it.id, name: it.name, price: it.price, quantity: it.quantity })),
      shippingCost
    );

    if (!result.ok) {
      setSubmitting(false);
      setSubmitError(result.message || 'Porudžbina nije uspela. Pokušajte ponovo.');
      return;
    }

    // Best-effort: send confirmation email. If it fails, the order itself is
    // already safely saved — don't block or scare the customer over this.
    await sendOrderConfirmationEmail({
      to_email: form.email,
      to_name: form.fullName,
      order_num: result.orderNum ?? '',
      items_text: items.map((it) => `${it.name} x${it.quantity} — ${formatRSD(it.price * it.quantity)}`).join('\n'),
      total_text: formatRSD(totalPrice + shippingCost),
      address: `${form.address}, ${form.city}`,
    });

    setSubmitting(false);
    setOrderSuccess(result.orderNum ?? 0);
    clearCart();
  };

  if (orderSuccess !== null) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="w-full px-4 md:px-6 py-16 md:py-24 text-center max-w-[1450px] mx-auto">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-green-100 rounded-full">
            <i className="ri-check-line text-green-600 text-2xl" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">
            Porudžbina #{orderSuccess} je primljena!
          </h1>
          <p className="text-sm text-foreground-700 mb-8 max-w-md mx-auto">
            Hvala na poverenju. Potvrda je poslata na vaš email, a mi ćemo vas kontaktirati uskoro u vezi sa dostavom.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-8 py-3 bg-foreground-950 text-white font-label text-sm font-medium rounded-full hover:bg-foreground-900 transition-colors"
          >
            Nazad na početnu
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="w-full px-4 md:px-6 py-16 md:py-24 text-center max-w-[1450px] mx-auto">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-background-100 rounded-full">
            <i className="ri-shopping-cart-line text-foreground-400 text-2xl" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">
            Vaša korpa je prazna
          </h1>
          <p className="text-sm text-foreground-700 mb-8 max-w-md mx-auto">
            Dodajte proizvode u korpu i nastavite sa kupovinom.
          </p>
          <Link
            to="/#products"
            className="inline-flex items-center gap-2 px-8 py-3 bg-foreground-950 text-white font-label text-sm font-medium rounded-full hover:bg-foreground-900 transition-colors"
          >
            <i className="ri-arrow-right-line" />
            Pogledaj proizvode
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="w-full px-4 md:px-6 py-8 md:py-12 max-w-[1450px] mx-auto">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-8">
          Korpa
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
          {/* Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex gap-4 bg-white rounded-lg border border-background-200 p-4"
              >
                <div className="w-24 h-24 flex-shrink-0 bg-background-100 rounded-lg overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <Link
                        to={`/product/${item.id}`}
                        className="font-label text-sm font-medium text-foreground-950 hover:text-amber-600 transition-colors line-clamp-1"
                      >
                        {item.name}
                      </Link>
                      <div className="mt-1 font-label text-sm font-bold text-foreground-950">
                        {formatRSD(item.price)}
                      </div>
                      {item.oldPrice && (
                        <div className="text-xs text-foreground-500 line-through">
                          {formatRSD(item.oldPrice)}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="w-8 h-8 flex items-center justify-center text-foreground-400 hover:text-red-500 transition-colors"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <div className="flex items-center border border-background-200 rounded-lg">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="w-8 h-8 flex items-center justify-center text-foreground-700 hover:bg-background-100 transition-colors"
                      >
                        <i className="ri-subtract-line" />
                      </button>
                      <span className="w-8 text-center text-sm font-label text-foreground-950">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8 flex items-center justify-center text-foreground-700 hover:bg-background-100 transition-colors"
                      >
                        <i className="ri-add-line" />
                      </button>
                    </div>
                    <div className="text-sm font-label font-bold text-foreground-950 ml-auto">
                      {formatRSD(item.price * item.quantity)}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={clearCart}
              className="text-sm text-foreground-600 hover:text-red-500 transition-colors flex items-center gap-2"
            >
              <i className="ri-delete-bin-line" />
              Isprazni korpu
            </button>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-background-200 p-6 sticky top-24">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                Pregled porudžbine
              </h2>

              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between text-sm text-foreground-700">
                  <span>Proizvodi ({items.reduce((s, i) => s + i.quantity, 0)})</span>
                  <span className="font-label">{formatRSD(totalPrice)}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-foreground-700">
                  <span>Dostava</span>
                  <span className="font-label text-green-600">
                    {shippingCost === 0 ? 'Besplatna' : formatRSD(shippingCost)}
                  </span>
                </div>
                <div className="border-t border-background-200 pt-3 flex items-center justify-between">
                  <span className="font-label font-semibold text-foreground-950">Ukupno</span>
                  <span className="font-label text-xl font-bold text-foreground-950">
                    {formatRSD(totalPrice + shippingCost)}
                  </span>
                </div>
              </div>

              <button
                onClick={() => setShowCheckout(true)}
                className="w-full py-3.5 bg-foreground-950 text-white font-label text-sm font-medium rounded-full hover:bg-foreground-900 transition-colors whitespace-nowrap"
              >
                Nastavi na naplatu
              </button>

              <div className="mt-4 space-y-2">
                {[
                  { icon: 'ri-truck-line', text: 'Dostava 2-4 radna dana' },
                  { icon: 'ri-shield-check-line', text: 'Garancija 5 godina' },
                  { icon: 'ri-refresh-line', text: 'Povrat novca 30 dana' },
                ].map((t) => (
                  <div key={t.text} className="flex items-center gap-2 text-xs text-foreground-600">
                    <i className={`${t.icon} text-amber-600`} />
                    <span className="font-label">{t.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Checkout popup */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold text-foreground-950">
                Informacije za dostavu
              </h3>
              <button
                onClick={() => setShowCheckout(false)}
                className="w-8 h-8 flex items-center justify-center text-foreground-600 hover:text-foreground-950"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="space-y-3 mb-2">
              <div>
                <input
                  type="text"
                  placeholder="Ime i prezime"
                  value={form.fullName}
                  onChange={setField('fullName')}
                  className={`w-full px-4 py-3 border rounded-lg text-sm font-label text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:border-amber-500 ${formErrors.fullName ? 'border-red-400' : 'border-background-200'}`}
                />
                {formErrors.fullName && <p className="text-xs text-red-500 mt-1">{formErrors.fullName}</p>}
              </div>
              <div>
                <input
                  type="email"
                  placeholder="Email adresa"
                  value={form.email}
                  onChange={setField('email')}
                  className={`w-full px-4 py-3 border rounded-lg text-sm font-label text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:border-amber-500 ${formErrors.email ? 'border-red-400' : 'border-background-200'}`}
                />
                {formErrors.email && <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>}
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Adresa"
                  value={form.address}
                  onChange={setField('address')}
                  className={`w-full px-4 py-3 border rounded-lg text-sm font-label text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:border-amber-500 ${formErrors.address ? 'border-red-400' : 'border-background-200'}`}
                />
                {formErrors.address && <p className="text-xs text-red-500 mt-1">{formErrors.address}</p>}
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Grad"
                  value={form.city}
                  onChange={setField('city')}
                  className={`w-full px-4 py-3 border rounded-lg text-sm font-label text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:border-amber-500 ${formErrors.city ? 'border-red-400' : 'border-background-200'}`}
                />
                {formErrors.city && <p className="text-xs text-red-500 mt-1">{formErrors.city}</p>}
              </div>
              <div>
                <input
                  type="tel"
                  placeholder="Telefon"
                  value={form.phone}
                  onChange={setField('phone')}
                  className={`w-full px-4 py-3 border rounded-lg text-sm font-label text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:border-amber-500 ${formErrors.phone ? 'border-red-400' : 'border-background-200'}`}
                />
                {formErrors.phone && <p className="text-xs text-red-500 mt-1">{formErrors.phone}</p>}
              </div>
            </div>

            {submitError && (
              <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
                {submitError}
              </div>
            )}

            <div className="flex items-center justify-between mb-4 mt-4">
              <span className="font-label font-semibold text-foreground-950">Ukupno:</span>
              <span className="font-label text-xl font-bold text-foreground-950">
                {formatRSD(totalPrice + shippingCost)}
              </span>
            </div>
            <button
              onClick={handleSubmitOrder}
              disabled={submitting}
              className="w-full py-3.5 bg-foreground-950 text-white font-label text-sm font-medium rounded-full hover:bg-foreground-900 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting && <i className="ri-loader-4-line animate-spin" />}
              {submitting ? 'Slanje...' : 'Pošalji porudžbinu'}
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}